import React, { Fragment, useContext } from "react"
import { useStaticQuery, graphql } from "gatsby"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import { rgba } from "polished"
import Link from "src/components/Link"
import Logo from "src/components/Logo"
import Grid from "src/components/Grid"
import ScrollListener from "src/components/ScrollListener"
import NotificationBanner from "src/components/NotificationBanner"
import ResponsiveComponent from "src/components/ResponsiveComponent"
import AnimatedIcon from "src/components/AnimatedIcon"
import MobileMenu from "src/components/MobileMenu"
import Button from "src/components/Button"
import AnimateHeight from "react-animate-height"
import { AppContext } from "src/state/AppState"
import { getSlugLink } from "src/utils/format"
import { colors, typography, animations, mq, util, globals } from "src/styles"

const showHide = false // show and hide header on scroll
export const headerHeight = (attr = "height", multiplier = 1) =>
  util.responsiveStyles(
    attr,
    74 * multiplier,
    74 * multiplier,
    74 * multiplier,
    74 * multiplier
  )
export const headerHeightCollapsed = (attr = "height") =>
  util.responsiveStyles(attr, 74, 74, 74, 74)
const mobileBreak = mq.largeBreakpoint

const Dropdown = styled.ul`
  list-style: none;
  position: absolute;
  top: 100%;
  // min-width: 200px;
  border-radius: 5px;
  box-shadow: ${globals.shadow()};
  background: ${colors.bgColor};
  ${typography.navStyle};
  font-weight: 600;
  letter-spacing: 0;
  padding: 10px 12px;
  visibility: hidden;
  opacity: 0;
  transition: visibility ${animations.mediumSpeed} ease-in-out,
    opacity ${animations.mediumSpeed} ease-in-out,
    transform ${animations.mediumSpeed} cubic-bezier(0.44, 0.24, 0.16, 1);
  ${({ loginDropdown }) =>
    loginDropdown
      ? `
		background: ${colors.textColor};
		padding: 7px 12px 8px;
		text-align: left;
		right: 0;
		a {
			padding: 6px 0;
			display: block;
			color: ${colors.white};
			&:hover {
				color: ${colors.mainColor};
			}
		}
	`
      : `
		background: ${colors.bgColor};
		padding: 10px 27px 11px 12px;
		text-align: left;
		left: -12px;
		a {
			padding: 3px 0;
			color: ${colors.lightTextColor};
			position: relative;
			display: inline-block;
			&:hover {
				color: ${colors.mainColor};
			}
			&:before {
				content: '';
				display: block;
				position: absolute;
				width: 5px;
				height: 5px;
				top: 50%;
				margin-top: -3px;
				border-bottom: 1px solid currentcolor;
				border-right: 1px solid currentcolor;
				transform: rotate(-45deg);
				left: 100%;
				opacity: 0;
				transition: transform ${animations.mediumSpeed} ease-in-out, opacity ${animations.mediumSpeed} ease-in-out;
			}
			&:hover {
				color: ${colors.mainColor};
				&:before {
					opacity: 1;
					transform: rotate(-45deg) translate(4px, 4px);
				}
			}
		}
	`}
  li {
    width: 100%;
    white-space: nowrap;
  }
`

const NavLink = styled(Link)`
  display: block;
  ${typography.navStyle}
  line-height: 1em;
  padding: 1em 0;
  position: relative;
  &:after {
    content: "";
    display: block;
    position: absolute;
    top: 75%;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${colors.greenBright};
    transform: scaleX(0);
    transform-origin: right center;
    transition: transform ${animations.mediumSpeed} ease-in-out;
  }
  transition: padding ${animations.mediumSpeed} ease-in-out,
    margin ${animations.mediumSpeed} ease-in-out,
    background ${animations.mediumSpeed} ease-in-out,
    opacity ${animations.mediumSpeed} ease-in-out,
    color ${animations.mediumSpeed} ease-in-out;
  ${({ hasAtf, active }) =>
    hasAtf
      ? `
			color: inherit;
			${
        !active
          ? `
				&:hover {
					&:after {
						transform-origin: left center;
						transform: scaleX(1);
					}
				}
			`
          : `
				&:after {
					transform-origin: left center;
					transform: scaleX(1);
				}
			`
      }
		`
      : `
			color: inherit;
			${
        !active
          ? `
				&:hover {
					&:after {
						transform-origin: left center;
						transform: scaleX(1);
					}
				}
			`
          : `
				&:after {
					transform-origin: left center;
					transform: scaleX(1);
				}
			`
      }
	`}
`

const Wrapper = styled.header`
  position: sticky;
  top: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
`

const HeaderWrapper = styled.div`
	display: flex;
	align-items: center;
	background: white;
	transition: height ${animations.mediumSpeed} ease-in-out,
							transform ${animations.mediumSpeed} ease-in-out,
							box-shadow ${animations.mediumSpeed} ease-in-out;
	${({ scrolled, hasAtf, mobileMenuOpen }) =>
    scrolled
      ? `
		${headerHeightCollapsed()}
		background: ${colors.white};
		color: ${colors.textColor};
		box-shadow: 0 1px 0 ${rgba(colors.textColor, 0.1)};
	`
      : `
		${headerHeight()}
		${
      hasAtf
        ? `
			color: ${colors.bgColor};
		`
        : `
			color: ${colors.textColor};
		`
    }
	`}
	${({ navVisible }) =>
    navVisible &&
    `
		transform: translate3d(0, -101%, 0);
	`}

	${mq.maxWidth(mobileBreak)} {
		${({ mobileMenuOpen }) =>
      mobileMenuOpen
        ? `
			background: transparent;
			box-shadow: none;
			color: ${colors.textColor};
			${headerHeight()}
		`
        : ""}
	}
`

const HeaderContent = styled(Grid)``

const HeaderLogo = styled(Logo)`
  ${util.responsiveStyles("width", 148, 148, 148, 148)}
  height: auto;
  transition: color ${animations.mediumSpeed} ease-in-out,
    width ${animations.mediumSpeed} ease-in-out;
  ${({ scrolled, hasAtf, mobileMenuOpen }) =>
    scrolled
      ? `
		color: ${colors.mainColor};
		${util.responsiveStyles("width", 148, 148, 148, 148)}
	`
      : `
		${
      !hasAtf
        ? `
			color: ${colors.mainColor};
		`
        : `
			color: ${colors.bgColor};
		`
    }
	`}

  ${mq.maxWidth(mobileBreak)} {
    ${({ mobileMenuOpen }) =>
      mobileMenuOpen
        ? `
			color: ${colors.mainColor};
			${util.responsiveStyles("width", 148, 148, 148, 148)}
		`
        : ""}
  }
`

const LogoCol = styled.div`
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  a.logo-link {
    display: inline-block;
    vertical-align: top;
    transition: none;
    max-width: 100%;
    ${util.responsiveStyles("margin-right", 24, 20, 18, 16)}
  }
`

const NavLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: baseline;
  display: flex;
  width: 100%;
  justify-content: ${({ alignment }) =>
    alignment === "right" ? "flex-end" : "flex-start"};
  li {
    position: relative;
    flex-shrink: 0;
    &:hover {
      a {
        &:after {
          transform: scaleX(1);
        }
      }
      ${Dropdown} {
        visibility: visible;
        opacity: 1;
      }
    }
    &:not(:last-of-type) {
      ${({ spacing }) =>
        spacing === "tight"
          ? `
				margin-right: 10px;
			`
          : `
				${util.responsiveStyles("margin-right", 24, 20, 18, 16)}
			`}
    }
  }
`

const MenuIcon = styled.div`
  display: none;
  margin-left: -10px;
  margin-right: -10px;
  cursor: pointer;
  display: block;
  vertical-align: top;
  transition: color ${animations.mediumSpeed} ease-in-out;
  > div {
    padding: 5px 10px;
    display: block;
    outline: none;
    border: none;
    background: transparent;
    appearance: none;
    color: inherit;
    cursor: pointer;
  }
  span {
    display: block;
  }
`

const MenuButtonWrapper = styled.li`
  display: none;
  cursor: pointer;
  display: block;
  vertical-align: top;
  transition: color ${animations.mediumSpeed} ease-in-out;
`

const MenuButton = styled(Button)`
  overflow: hidden;
  span {
    transition: opacity ${animations.mediumSpeed} ease-in-out,
      transform ${animations.mediumSpeed} ease-in-out;
    &.label-closed {
      position: absolute;
      top: 3px;
      left: 3px;
      right: 3px;
      bottom: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 1px;
      opacity: 0;
      transform: translateY(50%);
      ${({ menuOpen }) =>
        menuOpen
          ? `
				opacity: 1;
				transform: none;
			`
          : ""}
    }
    &.label-open {
      ${({ menuOpen }) =>
        menuOpen
          ? `
				opacity: 0;
				transform: translateY(-50%);
			`
          : ""}
    }
  }
`

const DropdownButton = styled(Button)`
  &:hover {
    transform: none;
    ~ ${Dropdown} {
      opacity: 1;
      visibility: visible;
    }
  }
`

const HeaderNotificationBanner = styled(NotificationBanner)`
  position: relative;
  z-index: 5;
  ${({ hidden }) =>
    hidden &&
    `
		opacity: 0;
	`}
`

const SectionPicker = styled.div`
  background: ${colors.textColor};
  color: ${colors.white};
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  padding-left: var(--site-margins);
  padding-right: var(--site-margins);
  a {
    &:last-of-type {
      margin-left: var(--site-margins);
    }
  }
`

const SectionLink = styled(Link)`
  border-bottom: 3px solid transparent;
  font-size: 14px;
  display: block;
  padding: 5px 0 3px;
  &:hover {
    border-color: ${colors.lightBlue};
  }
  ${({ current }) =>
    current
      ? `
		border-color: ${colors.lightBlue};
	`
      : `
		border-color: transparent;
	`}
`

const Header = ({
  location,
  hasAtf = false,
  collapsed,
  hideToggle = false,
}) => {
  const { sanitySiteSettings, allSanityMenus } = useStaticQuery(
    graphql`
      query {
        sanitySiteSettings {
          title
          bannerText
          bannerColor
          ctaLinks {
            customerLoansLogin {
              ...Link
            }
            customerLeasesLogin {
              ...Link
            }
            businessSignUp {
              ...Link
            }
            businessLogin {
              ...Link
            }
          }
        }
        allSanityMenus {
          edges {
            node {
              _id
              _key
              slug {
                current
              }
              items {
                _key
                itemLink {
                  ...Link
                }
                sublinks {
                  ...Link
                }
              }
            }
          }
        }
      }
    `
  )

  const {
    mobileMenu,
    toggleMobileMenu,
    closeMobileMenu,
    setSiteSection,
    siteSection,
  } = useContext(AppContext)

  const menus = allSanityMenus?.edges

  const menuType = {
    agnostic: "business-navigation",
    customers: "customer-navigation",
    business: "business-navigation",
    blog: "business-navigation",
  }

  const homeLink = {
    agnostic: "/",
    blog: "/",
    customers: "/for-customers",
    business: "/",
  }

  const navigation = menus.filter(
    menu => menu?.node?.slug?.current === menuType[siteSection]
  )[0]?.node?.items
  const siteSettings = sanitySiteSettings

  const siteTitle = siteSettings?.title
  const { bannerText, bannerColor, ctaLinks } = siteSettings

  const {
    customerLoansLogin,
    customerLeasesLogin,
    businessSignUp,
    businessLogin,
  } = ctaLinks

  const pathname = location
  const pageHasAtf = false // hasAtf

  return (
    <Fragment>
      <ScrollListener.Consumer>
        {({
          scrolledToTop,
          scrolledToBottom,
          scrollY,
          scrolledUp,
          hasScrolled,
          pageHeight,
        }) => {
          let scrolled = false
          if (collapsed) {
            scrolled = true
          } else {
            scrolled = !scrolledToTop && hasScrolled
          }
          return (
            <Wrapper
              hasAtf={pageHasAtf}
              navVisible={!scrolledUp && !scrolledToTop && showHide}
            >
              <AnimateHeight
                duration={300}
                delay={0}
                animateOpacity={false}
                height={mobileMenu ? 0 : "auto"}
              >
                <div>
                  {bannerText && (
                    <div>
                      <HeaderNotificationBanner
                        text={bannerText}
                        setTheme={bannerColor}
                      />
                    </div>
                  )}
                  {hideToggle === false && (
                    <SectionPicker id="top">
                      <SectionLink
                        current={siteSection !== "customers"}
                        to={homeLink.business}
                        onClick={() => {
                          setSiteSection("business")
                        }}
                      >
                        For Business
                      </SectionLink>
                      <SectionLink
                        current={siteSection === "customers"}
                        to={homeLink.customers}
                        onClick={() => {
                          setSiteSection("customers")
                        }}
                      >
                        For Customers
                      </SectionLink>
                    </SectionPicker>
                  )}
                </div>
              </AnimateHeight>
              <HeaderWrapper
                navVisible={!scrolledUp && !scrolledToTop && showHide}
                hasAtf={pageHasAtf}
                scrolled={scrolled}
                mobileMenuOpen={mobileMenu}
              >
                <HeaderContent
                  small="m [9] [3] m"
                  medium="m [7] [5] m"
                  large="m [16] [8] m"
                  vAlign="center"
                  hasAtf={pageHasAtf}
                  navVisible={!scrolledUp && !scrolledToTop && showHide}
                >
                  <LogoCol>
                    <div
                      style={{ display: "inline-block", verticalAlign: "top" }}
                    >
                      <Link
                        to={
                          (pathname === "home" ? "/" : "/" + pathname) ===
                          homeLink[siteSection]
                            ? homeLink[siteSection] + "#top"
                            : homeLink[siteSection]
                        }
                        title={siteTitle}
                        className="logo-link"
                        striphash={true}
                      >
                        <span onClick={closeMobileMenu}>
                          <HeaderLogo
                            scrolled={scrolled}
                            hasAtf={pageHasAtf}
                            mobileMenuOpen={mobileMenu}
                          />
                        </span>
                      </Link>
                    </div>
                    <NavLinks alignment="left">
                      <ResponsiveComponent
                        small={<li style={{ display: "none" }} />}
                        custom={{
                          breakpoint: mobileBreak,
                          content:
                            navigation &&
                            navigation.map((item, index) => {
                              const { itemLink } = item
                              const actionType = itemLink.type || "pageLink"
                              const linkSlug =
                                itemLink?.link?.content?.main?.slug?.current
                              if (!itemLink.title) {
                                return false
                              }

                              return (
                                <li key={"header-link-" + item._key}>
                                  <NavLink
                                    target={itemLink.newTab && "_blank"}
                                    external={itemLink.type === "externalLink"}
                                    scrolled={scrolled}
                                    hasAtf={pageHasAtf}
                                    to={
                                      actionType === "externalLink"
                                        ? itemLink.externalLink
                                        : getSlugLink(
                                            itemLink.link,
                                            false,
                                            itemLink.linkSection
                                          )
                                    }
                                    active={pathname === linkSlug}
                                    key={itemLink._key}
                                    hasDropdown={item?.sublinks?.length > 0}
                                  >
                                    {itemLink.title}
                                  </NavLink>
                                  {item.sublinks && item?.sublinks?.length > 0 && (
                                    <Dropdown>
                                      {item.sublinks.map(
                                        (dropdownLink, index) => (
                                          <li key={dropdownLink._key}>
                                            <Link
                                              target={
                                                dropdownLink.newTab && "_blank"
                                              }
                                              external={
                                                dropdownLink.type ===
                                                "externalLink"
                                              }
                                              scrolled={scrolled}
                                              to={
                                                dropdownLink.type ===
                                                "externalLink"
                                                  ? dropdownLink.externalLink
                                                  : getSlugLink(
                                                      dropdownLink.link,
                                                      false,
                                                      dropdownLink.linkSection
                                                    )
                                              }
                                              active={pathname === linkSlug}
                                              key={dropdownLink._key}
                                            >
                                              {dropdownLink.title}
                                            </Link>
                                          </li>
                                        )
                                      )}
                                    </Dropdown>
                                  )}
                                </li>
                              )
                            }),
                        }}
                      />
                    </NavLinks>
                  </LogoCol>
                  <div>
                    <NavLinks alignment="right" spacing="tight">
                      <ResponsiveComponent
                        small={
                          <MenuButtonWrapper id="mobile-menu-icon">
                            <MenuButton
                              size="small"
                              shape="square"
                              setTheme="lightGrey"
                              onClick={() => toggleMobileMenu(!mobileMenu)}
                              menuOpen={mobileMenu}
                              title="Toggle Navigation"
                              icon={
                                <MenuIcon id="mobile-menu-icon">
                                  <div
                                    onClick={() =>
                                      toggleMobileMenu(!mobileMenu)
                                    }
                                  >
                                    <AnimatedIcon
                                      size={20}
                                      icon={mobileMenu ? "close" : "menu"}
                                    />
                                  </div>
                                </MenuIcon>
                              }
                            ></MenuButton>
                          </MenuButtonWrapper>
                        }
                        large={
                          <>
                            <>
                              <li
                                css={css`
                                  position: relative;
                                  padding-top: 7px;
                                  padding-bottom: 7px;
                                `}
                              >
                                {(businessLogin.externalLink ||
                                  businessLogin.link) && (
                                  <Button
                                    size="small"
                                    setTheme="lightGrey"
                                    target={businessLogin.newTab && "_blank"}
                                    external={
                                      businessLogin.type === "externalLink"
                                    }
                                    to={
                                      businessLogin.type === "externalLink"
                                        ? businessLogin.externalLink
                                        : getSlugLink(
                                            businessLogin.link,
                                            false,
                                            businessLogin.linkSection
                                          )
                                    }
                                    key={businessLogin._key}
                                  >
                                    {businessLogin.title}
                                  </Button>
                                )}
                              </li>
                              <li
                                css={css`
                                  position: relative;
                                  padding-top: 7px;
                                  padding-bottom: 7px;
                                `}
                              >
                                <DropdownButton
                                  size="small"
                                  setTheme="lightGrey"
                                  icon="keyboard_arrow_down"
                                  iconPosition="right"
                                >
                                  Customer Login
                                </DropdownButton>
                                <Dropdown loginDropdown>
                                  {customerLeasesLogin && (
                                    <li>
                                      <Link
                                        target={
                                          customerLeasesLogin.newTab && "_blank"
                                        }
                                        external={
                                          customerLeasesLogin.type ===
                                          "externalLink"
                                        }
                                        to={
                                          customerLeasesLogin.type ===
                                          "externalLink"
                                            ? customerLeasesLogin.externalLink
                                            : getSlugLink(
                                                customerLeasesLogin.link,
                                                false,
                                                customerLeasesLogin.linkSection
                                              )
                                        }
                                        key={customerLeasesLogin._key}
                                      >
                                        {customerLeasesLogin.title}
                                      </Link>
                                    </li>
                                  )}
                                  {(customerLoansLogin.externalLink ||
                                    customerLoansLogin.link) && (
                                    <li>
                                      <Link
                                        target={
                                          customerLoansLogin.newTab && "_blank"
                                        }
                                        external={
                                          customerLoansLogin.type ===
                                          "externalLink"
                                        }
                                        to={
                                          customerLoansLogin.type ===
                                          "externalLink"
                                            ? customerLoansLogin.externalLink
                                            : getSlugLink(
                                                customerLoansLogin.link,
                                                false,
                                                customerLoansLogin.linkSection
                                              )
                                        }
                                        key={customerLoansLogin._key}
                                      >
                                        {customerLoansLogin.title}
                                      </Link>
                                    </li>
                                  )}
                                </Dropdown>
                              </li>
                              <li>
                                {(businessSignUp.externalLink ||
                                  businessSignUp.link) && (
                                  <Button
                                    size="small"
                                    target={businessSignUp.newTab && "_blank"}
                                    external={
                                      businessSignUp.type === "externalLink"
                                    }
                                    to={
                                      businessSignUp.type === "externalLink"
                                        ? businessSignUp.externalLink
                                        : getSlugLink(
                                            businessSignUp.link,
                                            false,
                                            businessSignUp.linkSection
                                          )
                                    }
                                    key={businessSignUp._key}
                                  >
                                    {businessSignUp.title}{" "}
                                  </Button>
                                )}
                              </li>
                            </>
                          </>
                        }
                      />
                    </NavLinks>
                  </div>
                </HeaderContent>
              </HeaderWrapper>
            </Wrapper>
          )
        }}
      </ScrollListener.Consumer>

      <ResponsiveComponent
        small={
          <MobileMenu
            open={mobileMenu}
            toggleMobileMenu={toggleMobileMenu}
            navLinks={navigation}
            pathname={pathname}
            pageType={siteSection}
            customerLoansLogin={customerLoansLogin}
            customerLeasesLogin={customerLeasesLogin}
            businessSignUp={businessSignUp}
            businessLogin={businessLogin}
            // footerColumn1={footerColumn1}
            // footerColumn2={footerColumn2}
          />
        }
        custom={{
          breakpoint: mobileBreak,
          content: <span />,
        }}
      />
    </Fragment>
  )
}

export default Header
